import Highcharts from 'highcharts';
import PptxGenJS from 'pptxgenjs';
import { buildDecileMatrixPalette } from '../DecileMatrixStyles';
import { svgToBase64Png } from '../Forecast/exportForecastToPowerPoint';
import { MatrixTermResult } from '../Matrix/MatrixTypes';
import { SeasonalMatrixVolatility } from './SeasonalMatrixTypes';

interface SeasonalMatrixExportOptions {
  title: string;
  chart: Highcharts.Chart | null;
  matrix: MatrixTermResult | null;
  volatility: SeasonalMatrixVolatility | null;
  currencySymbol: string;
}

function getDecileValue(matrix: MatrixTermResult, rank: number): number | undefined {
  return matrix.weightedDeciles.find((decile) => Math.abs(decile.rank - rank) < 0.001)?.value;
}

function formatPrice(value: number | undefined, currencySymbol: string): string {
  return value == null ? '' : `${currencySymbol}${value.toFixed(4)}`;
}

export async function exportSeasonalMatrixToPowerPoint(options: SeasonalMatrixExportOptions): Promise<void> {
  const { title, chart, matrix, volatility, currencySymbol } = options;

  const presentation = new PptxGenJS();
  presentation.layout = 'LAYOUT_WIDE';
  const slide = presentation.addSlide();

  if (chart) {
    const chartWidth = 816;
    const chartHeight = 624;

    // Temporarily resize the chart to the export dimensions, take its SVG, then restore it
    const originalWidth = chart.chartWidth;
    const originalHeight = chart.chartHeight;
    chart.setSize(chartWidth, chartHeight, false);
    const svgElement = chart.container.querySelector('svg');
    const svgMarkup = svgElement ? new XMLSerializer().serializeToString(svgElement) : '';
    chart.setSize(originalWidth, originalHeight, false);

    if (svgMarkup) {
      const chartImage = await svgToBase64Png(svgMarkup, chartWidth, chartHeight);
      slide.addImage({ data: chartImage, x: 0.3, y: 0.5, w: 8.5, h: 6.5 });
    }
  }

  if (matrix) {
    const headerOptions: PptxGenJS.TextPropsOptions = { bold: true, fontSize: 8, color: 'FFFFFF', fill: { color: '1B3A5C' } };
    const labelOptions: PptxGenJS.TextPropsOptions = { bold: true, fontSize: 8, align: 'left', color: '000000' };
    const valueOptions: PptxGenJS.TextPropsOptions = { fontSize: 8, align: 'right', color: '000000' };

    const tableRows: PptxGenJS.TableRow[] = [
      [{ text: 'Percentile', options: { ...headerOptions, align: 'left' } }, { text: 'Range', options: { ...headerOptions, align: 'right' } }],
      [{ text: 'Mean', options: labelOptions }, { text: formatPrice(matrix.weightedMean, currencySymbol), options: valueOptions }],
      [{ text: 'Median', options: labelOptions }, { text: formatPrice(getDecileValue(matrix, 0.5), currencySymbol), options: valueOptions }],
    ];

    // Bands from 90-100% down to 0-10%, coloured like the Matrix screen
    buildDecileMatrixPalette().forEach((band, index) => {
      const low = 90 - index * 10;
      const high = low + 10;
      const fillColor = band.color.replace('#', '');
      tableRows.push([
        { text: `${low}% - ${high}%`, options: { ...labelOptions, fill: { color: fillColor } } },
        { text: `${formatPrice(getDecileValue(matrix, low / 100), currencySymbol)} - ${formatPrice(getDecileValue(matrix, high / 100), currencySymbol)}`, options: valueOptions },
      ]);
    });

    slide.addTable(tableRows, {
      x: 9.0,
      y: 1.2,
      w: 3.8,
      colW: [1.4, 2.4],
      fontSize: 9,
      rowH: 0.38,
      valign: 'middle',
      border: { type: 'solid', pt: 0.5, color: 'CCCCCC' },
    });
  }

  if (volatility) {
    slide.addText(`Volatility Index: ${Math.round(volatility.index)} / 100`, { x: 9.0, y: 6.3, w: 3.8, h: 0.4, bold: true, fontSize: 12, color: '000000', isTextBox: true });
  }

  await presentation.writeFile({ fileName: `${title} Seasonal Matrix.pptx` });
}
